import { readCookie } from './readCookie';

const GCLID_COOKIE = '__ap_gclid';
const GBRAID_COOKIE = '__ap_gbraid';
const WBRAID_COOKIE = '__ap_wbraid';

export interface GoogleClickCookies {
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
}

/**
 * Reads the Google click identifiers so they can be forwarded to the Data
 * Manager API alongside the event.
 *
 * These live under our own `__ap_` prefix rather than in Google's `_gcl_*`
 * cookies: that format is undocumented and Google's own tags manage those
 * cookies, so writing them ourselves risks breaking the tracking that already
 * works. They are set at the CDN edge from the landing page URL, before any
 * script runs, which is the only point that survives an ad blocker — and the
 * identifier exists on that first request only.
 *
 * `gbraid` and `wbraid` are the privacy-preserving identifiers Google sends in
 * place of `gclid` on iOS app and web-to-app journeys. Usually one is present,
 * though Google may send `gclid` and `gbraid` on the same click, so all three
 * are read and forwarded. Any that belonged to an earlier click are cleared at
 * the edge when a new one arrives, so what is read here describes a single
 * click rather than a mix of visits.
 *
 * All three are absent for a visitor who did not arrive from a Google ad, which
 * is the overwhelming majority.
 *
 * Sent unhashed: Google matches on the identifier itself.
 */
export function getGoogleCookies(): GoogleClickCookies {
    if (typeof document === 'undefined') {
        return {};
    }

    return {
        gclid: readCookie(GCLID_COOKIE),
        gbraid: readCookie(GBRAID_COOKIE),
        wbraid: readCookie(WBRAID_COOKIE),
    };
}
